"use client";

import { Button } from "@cmp-src/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@cmp-src/primitives/Dialog";
import { Input } from "@cmp-src/primitives/input";
import { Label } from "@cmp-src/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@cmp-src/primitives/Select";
import type { ReportCatalog, ReportTile } from "@gettactic/api";
import React, { useState } from "react";
import { useReportBoardMutations } from "../hooks/useReportBoardMutations";
import { useReportBoards } from "../hooks/useReportBoards";
import { newTileId } from "../lib/boards";
import { describeTile } from "../lib/describeQuery";

/** What Explore hands over: a question or a dataset selection, no id yet. */
export type TileDraft = Pick<
  ReportTile,
  "view" | "query" | "records" | "chart"
>;

interface SaveToBoardDialogProps {
  catalog: ReportCatalog;
  draft: TileDraft;
  isOpen: boolean;
  onClose: () => void;
}

const NEW_BOARD = "__new__";

/** Adds the current Explore question to one of the user's boards, or a new one. */
export const SaveToBoardDialog: React.FC<SaveToBoardDialogProps> = ({
  catalog,
  draft,
  isOpen,
  onClose
}) => {
  const { data: boards } = useReportBoards();
  const { updateTiles, createBoard } = useReportBoardMutations();
  const editable = (boards?.boards ?? []).filter((b) => b.can_edit && b.id);
  // The first editable board unless one was picked; the boards usually load
  // after the dialog has opened.
  const [picked, setPicked] = useState<string | null>(null);
  const target = picked ?? editable[0]?.id ?? NEW_BOARD;
  const [newName, setNewName] = useState("");
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    const tile: ReportTile = {
      id: newTileId(),
      title,
      size: draft.view === "records" ? 4 : 2,
      view: draft.view,
      query: draft.query,
      records: draft.records,
      chart: draft.view === "chart" ? draft.chart : null,
      target: null
    };
    setIsSaving(true);
    setSaveError(null);
    try {
      const board = editable.find((b) => b.id === target);
      if (board?.id) {
        await updateTiles.mutateAsync({
          id: board.id,
          tiles: [...board.tiles, tile]
        });
      } else {
        await createBoard.mutateAsync({
          name: newName.trim() || "My board",
          visibility: "private",
          filters: { office_ids: [], period: null, filters: [] },
          tiles: [tile]
        });
      }
      onClose();
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "The board could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => (open ? undefined : onClose())}
    >
      <DialogContent className="z-modal max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Save to board</DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-neutral-500">
          {describeTile(catalog, {
            id: "",
            title: "",
            size: 2,
            target: null,
            ...draft
          })}
        </p>
        <div className="mt-3 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tile-title">Title (optional)</Label>
            <Input
              id="tile-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Board</Label>
            <Select value={target} onValueChange={setPicked}>
              <SelectTrigger className="h-9 text-[13px]">
                <SelectValue>
                  {editable.find((b) => b.id === target)?.name ?? "New board…"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-modal">
                {editable.map((b) => (
                  <SelectItem key={b.id ?? ""} value={b.id ?? ""}>
                    {b.name}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_BOARD}>New board…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {target === NEW_BOARD ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-board-name">New board name</Label>
              <Input
                id="new-board-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="My board"
              />
            </div>
          ) : null}
        </div>
        {saveError ? (
          <p className="mt-3 text-[13px] text-red-600">{saveError}</p>
        ) : null}
        <DialogFooter className="mt-5">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} isLoading={isSaving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
